import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import valid from 'card-validator';
import { trackEvent } from 'user-analytics';
import { trackPaymentRedirect } from 'metrics/user-analytics/errorTracker';
import { setAlert } from '@/actions';
import { cardPaymentAttempt } from '@/actions/payment';
import cieloEngine from 'payments/core/engines/cielo';
import useScript from 'hooks/useScript';
import usePurchase from 'hooks/store/usePurchase';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { validateCardFields } from 'utils/formValidations';
import { getExpirationMonths, getExpirationYears } from 'utils/formRenderers';
import { getSopScriptUrl, buildAddressAttributes } from 'utils/cielo';
import getRegisteredClientAddress from 'utils/purchase/getRegisteredClientAddress';
import { RESET_3DS_RETRY } from 'constants/ActionTypes';
import CieloTDCContext from './CieloTDCContext';

// Canonical "no address" state for the billing-address fields. Used both as the
// form's initial values and as the reset fallback when there's no registered
// address to restore — so a reset always clears user edits (see resetBillingAddress).
const EMPTY_BILLING_ADDRESS = {
  cep: '',
  estado: '',
  cidade: '',
  rua: '',
  numero: '',
  complemento: '',
  bairro: '',
};

/**
 * Manages the Cielo TDC integration with 3DS verification and provides context for Cielo credit card payments.
 *
 * @see https://www.notion.so/reservamos/Cielo-TDC-165cd6d27f7680ccb0d3f1ef11b01099?pvs=4
 */
const CieloTDCProvider = (props) => {
  const { children, purchaseToken: purchaseTokenProp } = props;

  // Get config from Redux instead of props to avoid re-render loops
  const cieloConfigFromRedux = useSelector((state) => state.whitelabelConfig.env.cielo);
  const config = useMemo(() => cieloConfigFromRedux || {}, [cieloConfigFromRedux]);
  const purchaseTokenFromRedux = useSelector((state) => state.purchase.get('token'));

  // Use prop if provided (for WebView), otherwise use Redux (for normal checkout)
  const purchaseToken = purchaseTokenProp || purchaseTokenFromRedux;

  const dispatch = useDispatch();
  const purchase = usePurchase();
  const [isLoading, setIsLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [sopAccessToken, setSOPAccessToken] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [hasErrorFetchingToken, setHasErrorFetchingToken] = useState(false);
  const [isRetryWith3DS, setIsRetryWith3DS] = useState(false);
  const isRetryWith3DSRef = useRef(false);

  const threeDSRetryTriggered = useSelector((state) => state.payment.get('threeDSRetryTriggered'));
  const threeDSRetryAmount = useSelector((state) => state.payment.get('threeDSRetryAmount'));

  const formikRef = useRef();
  const monthlySelectedPlanRef = useRef();
  const configRef = useRef(config);
  const buildSOPOptionsRef = useRef();

  /**
   * Builds SOP options for Silent Order Post tokenization.
   * @param {object} extraPaymentOptions - Additional options to pass to cardPaymentAttempt.
   * @returns {object} SOP configuration options.
   */
  const buildSOPOptions = (extraPaymentOptions = {}) => ({
    accessToken: sopAccessToken,
    environment: config.sopEnvironment || 'sandbox',
    language: config.sopLanguage || 'en',
    enableBinQuery: config.sopEnableBinQuery !== false,
    enableVerifyCard: config.sopEnableVerifyCard === true,
    cvvRequired: config.sopCvvRequired !== false,
    onSuccess: (response) => {
      const formValues = formikRef.current.values;
      if (!formValues.cardBrand) {
        setIsLoading(false);
        setIsRetryWith3DS(false);
        dispatch(setAlert('cielo.retry', 'error'));
        return;
      }
      const cardValues = {
        ...formValues,
        monthlySelectedPlan: monthlySelectedPlanRef.current,
        paymentToken: response.PaymentToken,
        cardBrand: response.Brand || formValues.cardBrand,
        addressAttributes: buildAddressAttributes(formValues),
      };
      const tokenToUse = purchaseToken || purchase.token;

      dispatch(
        cardPaymentAttempt(tokenToUse, cardValues, {
          enableSilentOrderPost: true,
          ...extraPaymentOptions,
        }),
      ).catch(() => {
        setIsLoading(false);
        setIsRetryWith3DS(false);
      });
      setIsLoading(false);
      setIsRetryWith3DS(false);
    },
    onError: () => {
      const tokenToUse = purchaseToken || purchase.token;
      trackEvent('SOPTokenizationError', {
        reason: 'tokenization_error',
        purchaseToken: tokenToUse,
        cardBrand: formikRef.current.values.cardBrand,
      });
      dispatch(setAlert('cielo.retry', 'error'));
      setIsLoading(false);
      setIsRetryWith3DS(false);
    },
    onInvalid: (validation) => {
      const tokenToUse = purchaseToken || purchase.token;
      const validationFields = validation ? Object.keys(validation) : [];
      trackEvent('SOPValidationError', {
        reason: 'invalid',
        purchaseToken: tokenToUse,
        invalidFields: validationFields,
      });
      dispatch(setAlert('cielo.retry', 'error'));
      setIsLoading(false);
      setIsRetryWith3DS(false);
    },
  });

  /**
   * Handles the first payment attempt skipping 3DS (when enableThreeDSOnRetry is active).
   * Uses SOP tokenization if available, otherwise falls back to backend tokenization.
   */
  const handleFirstAttemptSkipping3DS = () => {
    setIsLoading(true);

    if (config.enableSilentOrderPost) {
      if (typeof window.bpSop_silentOrderPost !== 'function') {
        dispatch(setAlert('cielo.reload', 'error'));
        setIsLoading(false);
        return;
      }
      window.bpSop_silentOrderPost(buildSOPOptions({ skipThreeDS: true }));
    } else {
      // Fallback: Send payment with backend tokenization (no SOP, no 3DS)
      const formValues = formikRef.current.values;
      if (!formValues.cardBrand) {
        setIsLoading(false);
        dispatch(setAlert('cielo.retry', 'error'));
        return;
      }
      const cardValues = {
        ...formValues,
        monthlySelectedPlan: monthlySelectedPlanRef.current,
        addressAttributes: buildAddressAttributes(formValues),
      };
      const tokenToUse = purchaseToken || purchase.token;

      dispatch(
        cardPaymentAttempt(tokenToUse, cardValues, {
          skipThreeDS: true,
        }),
      ).catch(() => {
        setIsLoading(false);
      });
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      holderName: '',
      cardNumber: '',
      expirationMonth: getExpirationMonths()[0].value,
      expirationYear: String(getExpirationYears()[0]),
      cvv2: '',
      cardBrand: '',
      cardBrandManual: false,
      includeBillingAddress: false,
      ...EMPTY_BILLING_ADDRESS,
    },
    onSubmit: () => {
      const shouldSkip3DSOnFirstAttempt =
        config.enableThreeDSOnRetry && config.enableThreeDS && !isRetryWith3DS;

      // FIRST ATTEMPT: skip 3DS when enableThreeDSOnRetry is active
      if (shouldSkip3DSOnFirstAttempt) {
        handleFirstAttemptSkipping3DS();
        return;
      }

      // RETRY ATTEMPT: execute 3DS after Cybersource rejection
      if (isRetryWith3DS) {
        if (!window.bpmpi_authenticate) {
          dispatch(setAlert('cielo.reload', 'error'));
          setIsLoading(false);
          return;
        }
        setIsLoading(true);
        trackPaymentRedirect({ provider: '3ds_cielo', isExpected: true });
        window.bpmpi_authenticate();
        return;
      }

      // NORMAL FLOW: original behavior when enableThreeDSOnRetry is not active
      if (config.enableThreeDS && !window.bpmpi_authenticate) {
        dispatch(setAlert('cielo.reload', 'error'));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      if (config.enableThreeDS) {
        trackPaymentRedirect({ provider: '3ds_cielo', isExpected: true });
        window.bpmpi_authenticate();
      } else if (config.enableSilentOrderPost) {
        if (typeof window.bpSop_silentOrderPost !== 'function') {
          dispatch(setAlert('cielo.reload', 'error'));
          setIsLoading(false);
          return;
        }
        window.bpSop_silentOrderPost(buildSOPOptions());
      }
    },
    validate: (values) => {
      const errors = validateCardFields(values);
      // Auto-detect the brand from the card number, unless the user picked one
      // manually via the brand selector (cardBrandManual).
      if (!errors.cardNumber && !values.cardBrandManual) {
        const cardBrand = valid.number(values.cardNumber).card.type;
        formik.setFieldValue('cardBrand', cardBrand);
      }

      // Validate address fields only when the user opted into the toggle.
      if (values.includeBillingAddress) {
        const normalize = (value) => (typeof value === 'string' ? value.trim() : value);
        const requiredAddressFields = ['cep', 'estado', 'cidade', 'rua', 'numero', 'bairro'];
        requiredAddressFields.forEach((field) => {
          if (!normalize(values[field])) {
            errors[field] = 'Required';
          }
        });

        const cep = normalize(values.cep);
        if (cep && !/^\d{8}$/.test(cep)) {
          errors.cep = 'CEP must be 8 digits';
        }
      }

      return errors;
    },
    validateOnChange: false,
  });

  // --- Registered billing address (INS-957) ------------------------------------
  // The cardholder's registered (IMS) address lives in `ticket.meta['client_address']`
  // and is exposed per-ticket on the purchase GET response body via TicketSerializer
  // (purchase INS-1053). Funnel already loads tickets through getPurchase, so we read
  // it off a ticket (NOT the purchase root — it never lives there). We seed the form
  // with it so the fields are ALWAYS prefilled; the "define different address" toggle
  // then controls editability + whether we resend it (see CieloWebViewForm). Off by
  // default keeps the ticket-meta address unpolluted.
  //
  // IMS -> form mapping:
  //   zip->cep · stateCode->estado · city->cidade · street->rua
  //   number->numero · complement->complemento · neighborhood->bairro
  const billingAddressPrefilledRef = useRef(false);
  const [defaultBillingAddress, setDefaultBillingAddress] = useState(null);
  const clientAddress = useMemo(() => getRegisteredClientAddress(purchase), [purchase]);

  React.useEffect(() => {
    if (billingAddressPrefilledRef.current) return;

    // When no ticket carries it (e.g. no clientAuth / tenant without IMS lookup) we
    // leave the fields empty — we don't invent an address.
    if (!clientAddress) return;

    billingAddressPrefilledRef.current = true;
    const normalized = {
      cep: clientAddress.zip ? String(clientAddress.zip).replace(/\D/g, '').slice(0, 8) : '',
      estado: clientAddress.stateCode || '',
      cidade: clientAddress.city || '',
      rua: clientAddress.street || '',
      numero: clientAddress.number ? String(clientAddress.number) : '',
      complemento: clientAddress.complement || '',
      bairro: clientAddress.neighborhood || '',
    };
    setDefaultBillingAddress(normalized);
    formik.setValues((prev) => ({ ...prev, ...normalized }), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientAddress]);

  // Restore the registered address, discarding any user edits, and flip the toggle OFF.
  // Invoked when the user toggles "define different address" OFF (see CieloWebViewForm).
  // Both mutations happen in ONE functional setValues so they can't race: doing the
  // flag and the address as two separate Formik calls let the reset's `prev` clobber the
  // flag back to `true` (toggle sprang back on). `includeBillingAddress: false` lives here
  // so the toggle also turns off cleanly even when there's no registered address to restore.
  const resetBillingAddress = useCallback(() => {
    // Fall back to EMPTY_BILLING_ADDRESS (not `{}`) when there's no registered
    // address, so the reset clears the user's edits instead of leaving them in
    // `prev`. With a registered address, its fields overwrite the edits either way.
    formikRef.current.setValues(
      (prev) => ({
        ...prev,
        ...(defaultBillingAddress || EMPTY_BILLING_ADDRESS),
        includeBillingAddress: false,
      }),
      false,
    );
  }, [defaultBillingAddress]);

  // Use refs to prevent multiple initializations
  const configSetRef = useRef(false);
  const threeDSTokenFetchedRef = useRef(false);
  const sopTokenFetchedRef = useRef(false);
  const { hasError: hasErrorLoading3dsScript } = useScript(config.threeDSScriptSrc, {
    shouldLoadScript:
      config.enabled && (config.enableThreeDS || config.enableThreeDSOnRetry) && !!accessToken,
    removeOnUnmount: true,
    onUnmount: () => {
      const cardinalScripts = document.querySelectorAll('script[src*="cardinalcommerce.com"]');
      cardinalScripts.forEach((script) => {
        script.remove();
      });
      formik.resetForm();
      formikRef.current = formik;
    },
  });
  const sopScriptUrl = useMemo(
    () => config.sopScriptUrl || getSopScriptUrl(config.sopEnvironment),
    [config.sopScriptUrl, config.sopEnvironment],
  );
  const { isLoaded: sopScriptLoaded, hasError: hasErrorLoadingSOPScript } = useScript(
    sopScriptUrl,
    {
      shouldLoadScript: config.enabled && config.enableSilentOrderPost && !!sopAccessToken,
      removeOnUnmount: true,
      onUnmount: () => {
        const sopScripts = document.querySelectorAll('script[src*="silentorderpost"]');
        sopScripts.forEach((script) => {
          script.remove();
        });
        formik.resetForm();
        formikRef.current = formik;
      },
    },
  );

  // Handle script load/error events
  React.useEffect(() => {
    if (!sopScriptLoaded) return;

    // Poll for script initialization with exponential backoff
    let attempts = 0;
    const maxAttempts = 10;
    const initialInterval = 100; // Start with 100ms
    let timeoutId;

    /**
     * Checks if the SOP script is ready by polling for the global function.
     * Uses exponential backoff to reduce polling frequency over time.
     */
    const checkScriptReady = () => {
      if (typeof window.bpSop_silentOrderPost === 'function') {
        setIsReady(true);
      } else if (attempts < maxAttempts) {
        attempts += 1;
        const delay = initialInterval * 1.5 ** attempts;
        timeoutId = setTimeout(checkScriptReady, delay);
      } else {
        // Script failed to initialize after max attempts
        setHasErrorFetchingToken(true);
      }
    };

    checkScriptReady();

    // Cleanup timeout on unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [sopScriptLoaded]);

  // Set ready state to true if neither 3DS nor SOP is enabled
  React.useEffect(() => {
    const neitherFlowEnabled = !config.enableThreeDS && !config.enableSilentOrderPost;
    if (neitherFlowEnabled) {
      setIsReady(true);
    }
  }, [config.enableThreeDS, config.enableSilentOrderPost]);

  // Listen for 3DS retry trigger from Redux (dispatched by paymentFailureHandler)
  React.useEffect(() => {
    const shouldRunRetry = threeDSRetryTriggered && config.enableThreeDSOnRetry;

    if (!shouldRunRetry) return;

    setIsRetryWith3DS(true);
    setIsLoading(true);

    if (!window.bpmpi_authenticate) {
      dispatch(setAlert('cielo.reload', 'error'));
      setIsLoading(false);
      setIsRetryWith3DS(false);
      dispatch({ type: RESET_3DS_RETRY });
      return;
    }

    // Ensure 3DS hidden inputs have correct values before authentication.
    // threeDSRetryAmount comes from the rejected payment response (most reliable).
    // Fallback chain: payment response amount → Redux purchase.total → purchase.amount
    const amount = threeDSRetryAmount || purchase.total || parseFloat(purchase.amount) || 0;
    if (amount > 0) {
      const amountInput = document.querySelector('.bpmpi_totalamount');
      if (amountInput) {
        amountInput.value = Math.round(amount * 100);
      }
    }

    // Refresh SOP token for the retry — the first attempt consumed the original token
    if (config.enableSilentOrderPost) {
      sopTokenFetchedRef.current = false;
      cieloEngine
        .getSOPAccessToken({ purchaseToken: purchaseToken || purchase.token })
        .then((token) => setSOPAccessToken(token))
        .catch(() => setHasErrorFetchingToken(true));
    }

    window.bpmpi_authenticate();
    dispatch({ type: RESET_3DS_RETRY });
  }, [
    threeDSRetryTriggered,
    config.enableThreeDSOnRetry,
    config.enableSilentOrderPost,
    dispatch,
    threeDSRetryAmount,
    purchase.total,
    purchase.amount,
    purchaseToken,
    purchase.token,
  ]);

  formikRef.current = formik;
  monthlySelectedPlanRef.current = purchase.monthlySelectedPlan;
  isRetryWith3DSRef.current = isRetryWith3DS;
  configRef.current = config;
  buildSOPOptionsRef.current = buildSOPOptions;

  const setBpmpiConfig = useCallback(() => {
    if (configSetRef.current) {
      return;
    }
    configSetRef.current = true;

    /**
     * Handles 3DS validation errors by tracking the event and dispatching an alert.
     * @param {*} eventType - The type of 3DS validation error event.
     * @returns {Function} - A function that handles the error event.
     */
    const on3dsValidationError = (eventType) => () => {
      const tokenToUse = purchaseToken || purchase.token;
      trackEvent('ThreeDSValidationError', {
        reason: eventType,
        purchaseToken: tokenToUse,
        cardBrand: formikRef.current.values.cardBrand,
      });
      dispatch(setAlert('cielo.retry', 'error'));
      setIsLoading(false);
      setIsRetryWith3DS(false);
    };

    // Set ready state to false when starting to configure
    setIsReady(false);
    window.bpmpi_config = () => {
      return {
        onReady: () => {
          setIsReady(true);
        },
        onSuccess: (event) => {
          const formValues = formikRef.current.values;
          const formValuesAreInvalid = !formValues.cardBrand;
          if (formValuesAreInvalid) {
            setIsLoading(false);
            setIsRetryWith3DS(false);
            dispatch(setAlert('cielo.retry', 'error'));
            return;
          }

          const threeDSValidationData = {
            cavv: event.Cavv,
            xid: event.Xid,
            eci: event.Eci,
            version: event.Version,
            referenceId: event.ReferenceId,
          };

          // If SOP is enabled, tokenize the card via SOP before making the payment
          if (configRef.current.enableSilentOrderPost) {
            if (typeof window.bpSop_silentOrderPost !== 'function') {
              dispatch(setAlert('cielo.reload', 'error'));
              setIsLoading(false);
              setIsRetryWith3DS(false);
              return;
            }
            window.bpSop_silentOrderPost(
              buildSOPOptionsRef.current({
                threeDSValidationData,
                isThreeDSRetry: isRetryWith3DSRef.current,
              }),
            );
            return;
          }

          // Fallback: If SOP is not enabled, proceed with backend tokenization
          const cardValues = {
            ...formValues,
            monthlySelectedPlan: monthlySelectedPlanRef.current,
            addressAttributes: buildAddressAttributes(formValues),
          };
          const tokenToUse = purchaseToken || purchase.token;

          dispatch(
            cardPaymentAttempt(tokenToUse, cardValues, {
              threeDSValidationData,
              isThreeDSRetry: isRetryWith3DSRef.current,
            }),
          ).catch(() => {
            setIsLoading(false);
            setIsRetryWith3DS(false);
          });
          setIsLoading(false);
          setIsRetryWith3DS(false);
        },
        onFailure: on3dsValidationError('failure'),
        onUnenrolled: on3dsValidationError('unenrolled'),
        onDisabled: on3dsValidationError('disabled'),
        onError: on3dsValidationError('error'),
        onUnsupportedBrand: on3dsValidationError('unsupportedBrand'),
        Environment: config.environment,
        Debug: config.debug,
      };
    };
    // Note: isRetryWith3DS is read via isRetryWith3DSRef to avoid stale closure issues
    // since setBpmpiConfig is guarded by configSetRef and only executes once
  }, [config.environment, config.debug, dispatch, purchase.token, purchaseToken]);

  const fetch3DSToken = useCallback(async () => {
    if (threeDSTokenFetchedRef.current) {
      return;
    }
    threeDSTokenFetchedRef.current = true;

    try {
      const tokenToUse = purchaseToken || purchase.token;
      const token = await cieloEngine.get3DSToken({ purchaseToken: tokenToUse });
      setAccessToken(token);
    } catch (err) {
      threeDSTokenFetchedRef.current = false; // Reset flag on error to allow retry
      setHasErrorFetchingToken(true);
    }
  }, [purchaseToken, purchase.token]);

  const fetchSOPAccessToken = useCallback(async () => {
    if (sopTokenFetchedRef.current) {
      return;
    }
    sopTokenFetchedRef.current = true;

    try {
      const tokenToUse = purchaseToken || purchase.token;
      const token = await cieloEngine.getSOPAccessToken({ purchaseToken: tokenToUse });
      setSOPAccessToken(token);
    } catch (err) {
      sopTokenFetchedRef.current = false; // Reset flag on error to allow retry
      setHasErrorFetchingToken(true);
    }
  }, [purchaseToken, purchase.token]);

  /** Flujo para carga inicial de 3DS
   *
   * 1. Declarar función bpmpi_config
   * 2. Fetch de token 3DS
   * 3. Renderizar el formulario de cielo con un loading state y el campo del token oculto
   * 4. Cargar el script de 3DS
   * 5. Activar el formulario de cielo para que se pueda interactuar
   */
  const mountCieloTDCForm = useCallback(() => {
    // Preload 3DS if enabled directly or needed for retry
    if (config.enableThreeDS || config.enableThreeDSOnRetry) {
      setBpmpiConfig();
      fetch3DSToken();
    }
    if (config.enableSilentOrderPost) {
      fetchSOPAccessToken();
    }
    // If neither is enabled, do nothing (form won't work but won't make API calls)
  }, [config, setBpmpiConfig, fetch3DSToken, fetchSOPAccessToken]);

  const submitForm = useCallback(async () => {
    formik.submitForm();
  }, [formik]);

  const contextValue = React.useMemo(
    () => ({
      accessToken,
      sopAccessToken,
      hasError: hasErrorFetchingToken || hasErrorLoading3dsScript || hasErrorLoadingSOPScript,
      submitForm,
      mountCieloTDCForm,
      form: formik,
      isLoading: isLoading || !isReady,
      config,
      isRetryWith3DS,
      resetBillingAddress,
    }),
    [
      accessToken,
      sopAccessToken,
      hasErrorFetchingToken,
      hasErrorLoading3dsScript,
      hasErrorLoadingSOPScript,
      submitForm,
      mountCieloTDCForm,
      formik,
      isLoading,
      isReady,
      config,
      isRetryWith3DS,
      resetBillingAddress,
    ],
  );

  return <CieloTDCContext.Provider value={contextValue}>{children}</CieloTDCContext.Provider>;
};

CieloTDCProvider.propTypes = {
  children: PropTypes.node.isRequired,
  purchaseToken: PropTypes.string,
};

/**
 * CieloTDCProviderGuard component.
 * Wraps the CieloTDCProvider component and ensures it is only rendered if the Cielo engine is enabled.
 * If the Cielo engine is not enabled, it render a context with empty values.
 */
const CieloTDCProviderGuard = ({ children, purchaseToken, config: configProp }) => {
  const env = useWhitelabelEnvs();
  // Render the full provider if Cielo is enabled (for both 3DS and SOP)
  if (env.cielo?.enabled) {
    return (
      <CieloTDCProvider purchaseToken={purchaseToken} config={configProp}>
        {children}
      </CieloTDCProvider>
    );
  }
  // If Cielo is not enabled, provide empty context
  return (
    <CieloTDCContext.Provider
      value={{
        accessToken: '',
        hasError: false,
        submitForm: () => {},
        mountCieloTDCForm: () => {},
        form: {},
        isLoading: false,
        config: env.cielo || {},
        isRetryWith3DS: false,
        resetBillingAddress: () => {},
      }}
    >
      {children}
    </CieloTDCContext.Provider>
  );
};

CieloTDCProviderGuard.propTypes = {
  children: PropTypes.node.isRequired,
  purchaseToken: PropTypes.string,
  config: PropTypes.object,
};

export default CieloTDCProviderGuard;
